import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuditLogProvider } from './contexts/AuditLogContext';
import { ToastProvider, ToastContainer } from './contexts/ToastContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { AdminDashboard } from './pages/AdminDashboard';
import { MortalityList } from './pages/MortalityList';
import { MorbidityList } from './pages/MorbidityList';
import { AssignmentDashboard } from './pages/AssignmentDashboard';
import { AuditForm } from './pages/AuditForm';
import { MyAudits } from './pages/MyAudits';
import { SupervisorReview } from './pages/SupervisorReview';
import { ReviewsDashboard } from './pages/ReviewsDashboard';
import { RecommendationFollowup } from './pages/RecommendationFollowup';
import { Layout } from './components/Layout';
import { config } from './config/environment';
import './App.css';

// Import mock data demo for development
import './utils/mockDataDemo';

function App() {
  // Use basename from centralized config (derived from PUBLIC_URL or apiBaseUrl)
  const basename = config.basename;

  // DEBUG: Log basename and current URL to find root cause of space issue
  console.log('🔍 DEBUG App.tsx - Basename Analysis:', {
    'basename': JSON.stringify(basename),
    'basename.length': basename.length,
    'basename.charCodes': Array.from(basename).map(c => c.charCodeAt(0)),
    'basename.hasSpaces': basename.includes(' '),
    'process.env.PUBLIC_URL': JSON.stringify(process.env.PUBLIC_URL),
    'window.location.pathname': JSON.stringify(window.location.pathname),
    'window.location.href': window.location.href,
    'config.basename': JSON.stringify(config.basename)
  });

  // Check if we're accessing the root URL
  const isRootAccess = window.location.pathname === '/';
  
  // If accessing root, redirect to subfolder
  if (isRootAccess) {
    console.log('🔍 DEBUG App.tsx - Root redirect:', {
      'basename': JSON.stringify(basename),
      'redirectTo': JSON.stringify(`${basename}/`)
    });
    window.location.href = `${basename}/`;
    return <div>Redirecting...</div>;
  }

  return (
    <AuthProvider>
      <NotificationProvider>
        <AuditLogProvider>
          <ToastProvider>
            <Router basename={basename}>
              <div className="App">
              <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/mortality" element={
              <ProtectedRoute>
                <Layout>
                  <MortalityList />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/morbidity" element={
              <ProtectedRoute>
                <Layout>
                  <MorbidityList />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['superuser']}>
                <Layout>
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/assignments" element={
              <ProtectedRoute>
                <Layout>
                  <AssignmentDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audits" element={
              <ProtectedRoute>
                <Layout>
                  <MyAudits />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/:id" element={
              <ProtectedRoute>
                <Layout>
                  <AuditForm />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/reviews" element={
              <ProtectedRoute allowedRoles={['reviewer', 'superuser']}>
                <Layout>
                  <ReviewsDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/review/:id" element={
              <ProtectedRoute allowedRoles={['reviewer', 'superuser']}>
                <Layout>
                  <SupervisorReview />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/recommendations" element={
              <ProtectedRoute allowedRoles={['superuser']}>
                <Layout>
                  <RecommendationFollowup />
                </Layout>
              </ProtectedRoute>
            } />
              </Routes>
              <ToastContainer />
            </div>
          </Router>
          </ToastProvider>
        </AuditLogProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;